import React from 'react';
import { Link } from 'react-router-dom';
import { useCMS } from '../context/CMSContext';
import SEO from '../components/SEO';
import Gallery from '../components/Gallery';

export default function Home() {
  const { getPage } = useCMS();
  const page = getPage('forside');

  if (!page) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="h-8 w-48 bg-brand-sand/40 rounded" />
          <div className="h-4 w-64 bg-brand-sand/30 rounded" />
        </div>
      </div>
    );
  }

  const { title, content, meta_title, meta_description } = page;

  // Hjælper til Cloudflare on-the-fly resizing af billeder
  const getResizedUrl = (url, width = 1200, quality = 80) => {
    return url.replace(
      'https://assets.auxodev.dk/assets/',
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto/assets/`
    );
  };

  const heroImage = 'https://assets.auxodev.dk/assets/19a97e0e-8e3c-4b11-bc4c-e5bcc52ff735/stable-environment.jpg';

  return (
    <>
      <SEO 
        title={title?.da || 'EquiGramVet'} 
        description={meta_description?.da} 
        isHome={true} 
      />

      {/* Hero Sektion */}
      <section className="relative overflow-hidden bg-brand-beige/40 border-b border-brand-sand/30 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Hero Tekst */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-sand/50 border border-brand-warm/40 text-brand-brown text-xs font-semibold uppercase tracking-wider w-fit shadow-xs">
              <i className="fa-regular fa-location-dot text-brand-dark" /> Kører i hele Sjælland &bull; Borup
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-brand-dark leading-tight">
              Veterinær kiropraktik til heste og hunde
            </h1>
            
            <div 
              className="text-brand-brown text-base md:text-lg leading-relaxed max-w-2xl prose prose-stone"
              dangerouslySetInnerHTML={{ __html: content?.da || '' }}
            />

            <div className="flex flex-wrap gap-4 mt-4">
              <Link
                to="/kontakt"
                className="px-6 py-3.5 bg-brand-dark text-brand-cream font-medium rounded-brand hover:bg-brand-brown transition-colors duration-200 shadow-md text-center focus:outline-none focus:ring-2 focus:ring-brand-dark focus:ring-offset-2"
              >
                Kontakt os
              </Link>
              <Link
                to="/kiropraktik"
                className="px-6 py-3.5 bg-brand-cream text-brand-dark border border-brand-sand font-medium rounded-brand hover:bg-brand-beige transition-colors duration-200 text-center focus:outline-none focus:ring-2 focus:ring-brand-sand focus:ring-offset-2"
              >
                Læs om behandlinger
              </Link>
            </div>
          </div>

          {/* Hero Billede */}
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] rounded-brand overflow-hidden border border-brand-sand/60 shadow-xl bg-brand-sand/20">
              <img
                src={getResizedUrl(heroImage, 800, 85)}
                alt="Roligt staldmiljø hos EquiGramVet"
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>
            {/* Lille svævende badge */}
            <div className="absolute -bottom-6 -left-6 bg-brand-cream border border-brand-sand/50 p-5 rounded-brand shadow-lg max-w-[240px] hidden sm:block">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-brand-beige flex items-center justify-center text-brand-dark shrink-0">
                  <i className="fa-regular fa-heart" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-sm text-brand-dark">Faglig omsorg</h4>
                  <p className="text-xs text-brand-brown mt-1">Behandling altid på dyrets præmisser og i roligt tempo.</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Værdier / Hvorfor vælge os */}
      <section className="py-16 md:py-24 bg-brand-cream">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-dark mb-4">
              Tryghed, faglighed og individuel behandling
            </h2>
            <p className="text-brand-brown text-sm md:text-base">
              Vi arbejder ud fra en dyb respekt for dyrets biomekanik og adfærd. Hos os er dit dyr i centrum, og vi tager os altid den nødvendige tid.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Værdi 1 */}
            <div className="p-8 rounded-brand bg-brand-beige/50 border border-brand-sand/60 flex flex-col gap-4 shadow-xs">
              <div className="w-12 h-12 rounded-brand bg-brand-sand/60 flex items-center justify-center text-brand-dark text-xl">
                <i className="fa-light fa-user-doctor" />
              </div>
              <h3 className="font-serif text-xl font-bold text-brand-dark">Autoriseret dyrlæge</h3>
              <p className="text-brand-brown text-sm leading-relaxed">
                Uddannet dyrlæge med certificering i kiropraktik. Det sikrer en sundhedsfaglig, anatomisk korrekt og tryg behandling af dine dyr.
              </p>
            </div>

            {/* Værdi 2 */}
            <div className="p-8 rounded-brand bg-brand-beige/50 border border-brand-sand/60 flex flex-col gap-4 shadow-xs">
              <div className="w-12 h-12 rounded-brand bg-brand-sand/60 flex items-center justify-center text-brand-dark text-xl">
                <i className="fa-light fa-spa" />
              </div>
              <h3 className="font-serif text-xl font-bold text-brand-dark">Faglig ro &amp; omsorg</h3>
              <p className="text-brand-brown text-sm leading-relaxed">
                Vi skaber en stressfri atmosfære. Behandlingen foregår altid roligt og tålmodigt, så dyret føler sig trygt og afslappet under hele forløbet.
              </p>
            </div>

            {/* Værdi 3 */}
            <div className="p-8 rounded-brand bg-brand-beige/50 border border-brand-sand/60 flex flex-col gap-4 shadow-xs">
              <div className="w-12 h-12 rounded-brand bg-brand-sand/60 flex items-center justify-center text-brand-dark text-xl">
                <i className="fa-light fa-van-shuttle" />
              </div>
              <h3 className="font-serif text-xl font-bold text-brand-dark">Mobil i hele Sjælland</h3>
              <p className="text-brand-brown text-sm leading-relaxed">
                Udekørende kiropraktor baseret i Borup. Vi kører ud til stalde og private i hele Sjælland, og laver gerne specialaftaler for resten af Danmark.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Præsentation af Ydelser */}
      <section className="py-16 md:py-24 bg-brand-beige/40 border-t border-b border-brand-sand/50">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
            <div className="lg:col-span-7">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-brown block mb-2">
                Specialiseret Behandling
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-dark mb-4">
                Kiropraktik til heste og hunde
              </h2>
              <p className="text-brand-brown text-sm md:text-base">
                Veterinær kiropraktik fokuserer på at genskabe optimal ledbevægelighed, løsne spændinger i hvirvelsøjle og bevægeapparat samt støtte nervesystemets frie funktion.
              </p>
            </div>
            <div className="lg:col-span-5 lg:flex lg:justify-end">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-brand-dark text-brand-cream text-sm font-medium rounded-brand hover:bg-brand-brown transition-colors duration-200 shadow-sm"
              >
                Book en konsultation <i className="fa-regular fa-arrow-right text-xs" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-none">
            {/* Ydelse 1: Kiropraktik til heste */}
            <Link 
              to="/kiropraktik"
              className="group p-8 rounded-brand bg-brand-cream border border-brand-sand hover:border-brand-brown transition-all duration-300 flex flex-col gap-5 shadow-sm hover:shadow-md"
            >
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-brand bg-brand-beige flex items-center justify-center text-brand-dark text-xl group-hover:bg-brand-dark group-hover:text-brand-cream transition-colors duration-300">
                  <i className="fa-light fa-horse-head" />
                </div>
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-brand-sand/40 text-brand-brown">
                  Heste
                </span>
              </div>
              <h3 className="font-serif text-2xl font-bold text-brand-dark group-hover:text-brand-brown transition-colors duration-200">
                Kiropraktik til heste
              </h3>
              <p className="text-brand-brown/90 text-sm leading-relaxed">
                Genskaber normal bevægelighed i led og hvirvelsøjle. Afhjælper låsninger, muskelspændinger, modvilje i ridning og nedsat præstationsevne.
              </p>
              <span className="text-xs font-semibold text-brand-dark mt-auto flex items-center gap-2 pt-2 group-hover:translate-x-1 transition-transform duration-200">
                Læs om behandlingen <i className="fa-regular fa-arrow-right text-xs" />
              </span>
            </Link>

            {/* Ydelse 2: Kiropraktik til hunde */}
            <Link 
              to="/kiropraktik-til-hunde"
              className="group p-8 rounded-brand bg-brand-cream border border-brand-sand hover:border-brand-brown transition-all duration-300 flex flex-col gap-5 shadow-sm hover:shadow-md"
            >
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-brand bg-brand-beige flex items-center justify-center text-brand-dark text-xl group-hover:bg-brand-dark group-hover:text-brand-cream transition-colors duration-300">
                  <i className="fa-light fa-dog" />
                </div>
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-brand-sand/40 text-brand-brown">
                  Hunde
                </span>
              </div>
              <h3 className="font-serif text-2xl font-bold text-brand-dark group-hover:text-brand-brown transition-colors duration-200">
                Kiropraktik til hunde
              </h3>
              <p className="text-brand-brown/90 text-sm leading-relaxed">
                Blid ledmobilisering til alle hunde. Optimerer bevægelighed, lindrer ryg- og ledsmerter samt øger livskvaliteten hos såvel familie- som arbejdeshunde.
              </p>
              <span className="text-xs font-semibold text-brand-dark mt-auto flex items-center gap-2 pt-2 group-hover:translate-x-1 transition-transform duration-200">
                Læs om behandlingen <i className="fa-regular fa-arrow-right text-xs" />
              </span>
            </Link>
          </div>

          {/* Særtilbud sektion for 2026 */}
          <div className="mt-12 p-8 rounded-brand bg-brand-cream border-2 border-brand-sand/70 shadow-xs">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-beige flex items-center justify-center text-brand-dark shrink-0">
                  <i className="fa-regular fa-tag text-lg" />
                </div>
                <div>
                  <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-brown mb-1">
                    Resten af 2026
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl font-bold text-brand-dark">
                    Opstarts særtilbud på kiropraktik
                  </h3>
                  <p className="text-brand-brown text-sm mt-1 max-w-2xl leading-relaxed">
                    Hest fra 899.- og hund fra 399.- for første behandling. Få 20% rabat ved efterfølgende hest/hund samme sted. Priser er inkl. moms.
                  </p>
                </div>
              </div>
              <Link
                to="/priser"
                className="whitespace-nowrap px-6 py-3 bg-brand-dark text-brand-cream text-sm font-medium rounded-brand hover:bg-brand-brown transition-colors duration-200 shadow-sm"
              >
                Se alle priser &amp; vilkår
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Galleri Sektion */}
      <section className="py-16 md:py-24 bg-brand-cream">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-dark mb-4">
              Billeder fra hverdagen
            </h2>
            <p className="text-brand-brown text-sm md:text-base">
              Se et udvalg af billeder fra vores behandlinger og det rolige staldmiljø, hvor vi arbejder med dyrene.
            </p>
          </div>

          {/* Vi tilkobler galleriet "hestebehandling" fra CMS'et */}
          <Gallery gallerySlug="hestebehandling" />
        </div>
      </section>
    </>
  );
}
